
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';
import { Formatters } from '../../../../../gen-src/Localize';
import { DataQualityWarningFragment, DataQualityWarningAssetFragment } from '../../../../../gen-src/queries';
import { TableauUser } from '../UserDisplayComponent';
import { DataQualityWarningIndicator } from '../dataQualityIndicators/DataQualityIndicatorFragments';
import { DataQualityDetailPanelSide, DataQualityIndicatorAssetType } from '../dataQualityIndicators/DataQualityIndicatorSharedHelpers';
import { assertUnreachable, filterOutNullAndUndefined } from '../utils/TypescriptHelpers';
import { DataQualityDetailProps } from './DataQualityDetail';
import { DataQualityStatusChip } from './DataQualityStatusChip';
import { stringToDataQualityType } from './DataQualityType';

/**
 * @deprecated TFS-1478812
*/
export type DataQualityAssetType = DataQualityIndicatorAssetType

/** Data Quality Warnings that come from the GraphQL API */
/**
 * @deprecated TFS-1478812
*/
export type GraphDataQualityWarning = DataQualityWarningFragment & DataQualityWarningAssetFragment;

/**
 * @deprecated TFS-1478812
*/
export type DataQualityWarningGraphqlOrIndicator = GraphDataQualityWarning | DataQualityWarningIndicator;

/**
 * @deprecated TFS-1478812
*/
export interface RenderWarningsFromGraphAndVizportalProps {
  readonly anchoredWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly upstreamWarnings: ReadonlyArray<GraphDataQualityWarning>;
  readonly iconOnly: boolean;
  readonly urls: SiteUrls;
}

/**
 * @deprecated TFS-1478812
*/
export interface RenderWarningsFromGraphProps {
  readonly anchoredDataQualityWarnings: ReadonlyArray<GraphDataQualityWarning>;
  readonly upstreamDataQualityWarnings: ReadonlyArray<GraphDataQualityWarning>;
  readonly iconOnly: boolean;
  readonly urls: SiteUrls;
  readonly side?: DataQualityDetailPanelSide;
}

/**
 * @deprecated TFS-1478812
*/
export interface RenderWarningsProps {
  readonly details: ReadonlyArray<DataQualityDetailProps>;
  readonly iconOnly: boolean;
  readonly side?: DataQualityDetailPanelSide;
}

/**
 * @deprecated TFS-1478812
*/
export function RenderWarningsFromGraphAndVizportal(props: RenderWarningsFromGraphAndVizportalProps): JSX.Element | null {
  const upstream = props.upstreamWarnings.map(f => mapFragmentToDetail(f, false, props.urls));
  return <RenderWarnings {...props} details={props.anchoredWarnings.concat(upstream)} />;
}

/**
 * @deprecated TFS-1478812
*/
export function RenderWarningsFromGraph(props: RenderWarningsFromGraphProps): JSX.Element | null {
  const fragments = props.anchoredDataQualityWarnings;
  const anchored = mapActiveFragmentsOrIndicatorsToDetails(fragments, true, props.urls);

  const upstream = props.upstreamDataQualityWarnings.map(f => mapFragmentToDetail(f, false, props.urls));

  return <RenderWarnings {...props} details={anchored.concat(upstream)} />;
}

function RenderWarnings(props: RenderWarningsProps): JSX.Element | null {
  if (props.details.length > 0) {
    return <DataQualityStatusChip {...props} />;
  }

  return null;
}

/**
 * @deprecated TFS-1478812
*/
export function mapActiveFragmentsOrIndicatorsToDetails(
  warnings: ReadonlyArray<DataQualityWarningGraphqlOrIndicator>,
  onContentItem: boolean,
  urls?: SiteUrls
): ReadonlyArray<DataQualityDetailProps> {
  return filterOutNullAndUndefined(
    warnings.filter(w => w.isActive)
      .map(f => mapFragmentOrIndicatorToDetail(f, onContentItem, urls))
  );
}

function mapFragmentOrIndicatorToDetail(either: DataQualityWarningGraphqlOrIndicator, onContentItem: boolean, urls?: SiteUrls):
  DataQualityDetailProps | null {

  if (!either.isActive) {
    return null;
  }

  if ('__typename' in either) {
    return mapFragmentToDetail(either, onContentItem, urls);
  } else {
    return mapIndicatorToDetail(either, onContentItem, urls);
  }
}

function mapIndicatorToDetail(
  indicator: DataQualityWarningIndicator,
  onContentItem: boolean,
  urls?: SiteUrls): DataQualityDetailProps {

  return {
    onContentItem,
    isSevere: indicator.isSevere,
    assetName: indicator.assetName,
    assetType: indicator.assetType,
    assetUrl: urls ? indicator.assetUrl(urls) : undefined,
    author: authorFromIndicator(indicator),
    localizedDate: indicator.updatedAt ? Formatters.formatMediumDateTimeWithoutSeconds(new Date(indicator.updatedAt)) : '',
    type: stringToDataQualityType(indicator.type),
    message: indicator.message
  };
}

function mapFragmentToDetail(
  fragment: DataQualityWarningFragment & DataQualityWarningAssetFragment,
  onContentItem: boolean,
  urls?: SiteUrls,
): DataQualityDetailProps {
  return {
    onContentItem,
    assetName: assetName(fragment),
    assetType: assetType(fragment),
    assetUrl: urls ? assetUrl(urls, fragment) : undefined,
    author: authorFromWarning(fragment),
    localizedDate: Formatters.formatMediumDateTimeWithoutSeconds(new Date(fragment.updatedAt)),
    type: stringToDataQualityType(fragment.warningType),
    message: fragment.message,
    isSevere: fragment.isSevere
  };
}

function authorFromIndicator(indicator: IDataQualityIndicator): TableauUser {
  return {
    name: indicator.owner.displayName,
    username: indicator.owner.username,
    domain: indicator.owner.domainName || null,
  };
}

function authorFromWarning(warning: DataQualityWarningFragment): TableauUser {
  return {
    name: warning.author && warning.author.name || warning.authorDisplayName,
    username: warning.author && warning.author.username,
    domain: warning.author && warning.author.domain,
  };
}

function assetName(fragment: DataQualityWarningAssetFragment): string {
  if (fragment.asset && fragment.asset.name !== null) {
    return fragment.asset.name;
  } else {
    return '';
  }
}

function assetType(fragment: DataQualityWarningAssetFragment): DataQualityAssetType | null {
  if (fragment.asset && fragment.asset.__typename !== null) {
    return fragment.asset.__typename;
  } else {
    return null;
  }
}

function assetUrl(urls: SiteUrls, fragment: DataQualityWarningAssetFragment): string {
  if (!fragment.asset) {
    return '';
  }

  const asset = fragment.asset;

  switch (asset.__typename) {
    case 'Flow':
      return urls.flowUrl(asset.vizportalUrlId);
    case 'PublishedDatasource':
      return urls.datasourceUrl(asset.vizportalUrlId);
    case 'DatabaseServer':
    case 'CloudFile':
    case 'File':
    case 'Column':
    case 'WebDataConnector':
      return urls.catalogDatabaseUrl(asset.id);
    case 'VirtualConnectionTable':
    case 'DatabaseTable':
      return urls.catalogTableUrl(asset.id);
    case 'VirtualConnection':
      return urls.virtualConnectionUrl(asset.id);
    default:
      assertUnreachable(asset);
      return '';
  }
}
